/**
 * What a group of scrapes has reached, and what its target counts (5 Oct 2026).
 *
 * A group's target could count every saved property, or Sold STC only. The scrapers have
 * saved Under Offer as its own status since 28 Sep 2026, and a mailing of "sale agreed"
 * homes takes both, so a target can now count Sold STC and Under Offer together. One place
 * for the sums and the wording, used by the groups list, the manage box, the add box and
 * the badge.
 */
import { mailableFromSummary } from "../models/mailability";
import { msOf, searchReading } from "./scrapeRunning";

/** The kinds of target, in the order the form offers them. */
export const TARGET_TYPES = [
  { value: "none", option: "No Target" },
  { value: "total", option: "Total Properties", short: "total", heading: "Total" },
  { value: "sstc", option: "SSTC Properties", short: "SSTC", heading: "SSTC" },
  { value: "sstc_uo", option: "SSTC + Under Offer Properties", short: "SSTC + UO", heading: "SSTC + Under Offer" },
];

const byValue = (type) => TARGET_TYPES.find((t) => t.value === type);

/** "SSTC", "SSTC + UO" or "total": beside a number, as in "Target: 500 SSTC + UO". */
export const targetShortLabel = (type) => byValue(type)?.short || "total";

/** "SSTC", "SSTC + Under Offer" or "Total": a heading, as in "Target SSTC Count". */
export const targetHeading = (type) => byValue(type)?.heading || "Total";

// A scrape's own counts, finished (top level) or still running (under progress).
const figure = (scrape, field) => scrape?.[field] ?? scrape?.progress?.[field] ?? 0;

const savedOf = (scrape) => scrape.propertiesFound || scrape.successfulProperties || scrape.progress?.successfulProperties || 0;

/** The mailing rule's one switch for single-sale matches (models/mailability.js). */
const WITH_SINGLE = Object.freeze({ includeSingleEvidence: true });

/**
 * How many of ONE scrape's homes the mailing rule would add, from the count by mailing reason
 * stored on the scrape when it finished (functions/scrapes/matchSummary.js); null when the
 * scrape has no such count: it has not finished, or it finished before the counts existed and
 * has not been counted yet. The Scrapes list's Mailable column and a group's row both ask
 * this, so the two cannot disagree about a scrape.
 * @param {Object} opts the mailing rule's switches (models/mailability.js). None: the mailing default.
 * @returns {{total: number, sstc: number, underOffer: number}|null}
 */
export function mailableOfScrape(scrape, opts = {}) {
  return countStands(scrape) ? mailableFromSummary(scrape.matchSummary, opts) : null;
}

/**
 * The two ways a scrape STOPS without finishing. Named, never "anything that is not running": a
 * status this page does not know may be a scrape that is still saving homes.
 */
export const STOPPED_STATUSES = Object.freeze(["failed", "cancelled"]);
export const isStopped = (scrape) => Boolean(scrape) && STOPPED_STATUSES.includes(scrape.status);

/**
 * TWO NUMBERS, from what the scrapers do (the Scraper's `jobQueue.js` and `worker.js`, 12 Oct 2026):
 *  - A scraper goes on for ONE TO TWO MINUTES after a scrape is cancelled: it looks for the cancel only before
 *    each search and at every fifth home, and until it looks it scrapes, saves and writes its heartbeat every
 *    ten seconds. Then it writes nothing more. (A failure is one write; the last heartbeat is before it.) So
 *    the page asks about a stopped scrape only when its last write is THREE MINUTES old: `SETTLE_MS`.
 *  - `heartbeat` and `startedAt` are the SCRAPER BOX's clock; the count's `computedAt` is the server's. A count
 *    stands only if it was made at least ONE MINUTE after the scraper's last write, by those two clocks:
 *    `CLOCK_ALLOWANCE_MS`. The minute is what stops a count made BEFORE the last save from standing when a
 *    box's clock runs slow, and an earlier run's count from standing after a short re-run.
 * What they tolerate: a box within a minute of the server; a browser up to two minutes fast. A count this
 * page asked for is always made three minutes or more after the heartbeat it read, whatever the box's skew,
 * because the ask and the test go by the same heartbeat.
 */
export const SETTLE_MS = 3 * 60 * 1000;
export const CLOCK_ALLOWANCE_MS = 60 * 1000;
/** Asks about one scrape in one page load, of both kinds together: a bound that needs no clock to be right. */
export const MAX_ASKS_PER_SCRAPE = 3;

/**
 * How long after the function has answered a count is given to ARRIVE (it comes by the list's own listener, as a
 * rule within a second or two). After that, a scrape that was asked about for this run and still has no count that
 * stands will not be asked about again in this page load: it is said to be stuck, not "being counted".
 */
export const ARRIVAL_GRACE_MS = 30 * 1000;

/**
 * When the scraper last wrote to a scrape's record: the LATER of its heartbeat and when it started. null: no sign
 * of either.
 * ⚠️ The later, not "the heartbeat, else the start". A box writes the start time afresh at every attempt, before it
 * can stop, and the heartbeat stays at the last progress write. So a running scrape whose box died an hour ago,
 * taken again by a box and stopped before that box's first heartbeat, holds an hour-old heartbeat and a start time
 * of seconds ago: read by the heartbeat it was "still" at once (a count asked for while its box wound down), and a
 * count made between the two times stood for good (the review of the third commit, finding B).
 */
export const lastRunOf = (scrape) => {
  const beat = msOf(scrape && scrape.heartbeat);
  const began = msOf(scrape && scrape.startedAt);
  if (beat === null) return began;
  return began === null ? beat : Math.max(beat, began);
};

/**
 * Whether the count by mailing reason on a scrape's record can be read as its count NOW (12 Oct 2026).
 *  - completed: yes, as it always was. It is written when the scrape completes, and written again if
 *    it completes again.
 *  - failed or cancelled: the count is asked for by the page (the function counts any scrape it is
 *    asked about). Until 12 Oct 2026 it never was, so a stopped scrape's homes were in NO mailable
 *    figure. ⚠️ It stands only if it was made at least a minute AFTER the scraper last wrote
 *    (CLOCK_ALLOWANCE_MS): the scrape page's Retry runs a stopped scrape again under the same id, and
 *    a scraper goes on saving for a minute or two after a cancel; a count made before the last save is
 *    the count of a scrape that has since saved more. Such a count is not shown and is asked for again.
 *  - anything else (running, claimed, waiting, a status nobody knows): no. A running scrape is still
 *    growing, and an old count must never stand beside a running bar.
 */
export function countStands(scrape) {
  if (!scrape || !scrape.matchSummary) return false;
  if (scrape.status === "completed") return true;
  if (!isStopped(scrape)) return false;
  const counted = msOf(scrape.matchSummary.computedAt);
  if (counted === null) return false;
  const ran = lastRunOf(scrape);
  return ran === null || counted - ran >= CLOCK_ALLOWANCE_MS;
}

/** Whether any of these scrapes is a stopped one waiting for its count: while one is, a row that says so must be drawn again as time passes. */
/**
 * Whether the scraper's clock and this browser's DISAGREE about a stopped scrape: its last write reads more than
 * a minute in this browser's future, so "three minutes after it" is further off than three minutes. Then the wait
 * is not "a moment": it lasts as long as the clocks are apart, and the row says that instead.
 */
export const clocksDisagree = (scrape, nowMs) => { const at = stillAt(scrape); return at !== null && at - nowMs > SETTLE_MS + CLOCK_ALLOWANCE_MS; };

/**
 * WHERE A SCRAPE STANDS with its count, in one word. ONE answer, for a group's row, the hover of a scrape's figure
 * and the list's total: they gave two different answers of one scrape until they shared this.
 *   counted   a count stands on its record
 *   running   under way, or waiting to run: counted when it finishes
 *   settling  stopped a moment ago: asked about once its scraper has been still for three minutes
 *   clock     stopped, and its scraper's clock and this browser's disagree: this page cannot tell when to ask
 *   counting  finished or stopped, and its count has been asked for, or is about to be
 *   stuck     it needs a count and will NOT be asked about again in this page load (`stuckIds`, from the hook)
 *   status    a status this page does not know: never counted, never asked about
 *   nothing   stopped, and its record says it saved no homes: nothing to ask about
 * `nowMs`: the browser's clock; with none, a stopped scrape cannot be told "settling" from "counting".
 */
export function countState(scrape, nowMs, stuckIds) {
  if (countStands(scrape)) return "counted";
  const status = scrape && scrape.status;
  if (status === "running" || status === "claimed" || status === "pending") return "running";
  const stuck = Boolean(stuckIds && scrape && stuckIds.has(scrape.id));
  if (status === "completed") return stuck ? "stuck" : "counting";
  if (!isStopped(scrape)) return "status";
  if (!(savedOf(scrape) > 0)) return "nothing";
  if (stuck) return "stuck";
  if (nowMs !== undefined && !readyToCount(scrape, nowMs)) return clocksDisagree(scrape, nowMs) ? "clock" : "settling";
  return "counting";
}

/**
 * Whether any of these scrapes stopped a moment ago and is waiting for its scraper to be still: while one is, a
 * row that says so must be drawn again as time passes. ⚠️ Only that: not a scrape that is stuck, or whose clocks
 * disagree, or whose count has been asked for. A timer that ran for those ran for as long as the tab was open.
 */
export const anyStoppedWaiting = (scrapes, nowMs, stuckIds) => (scrapes || []).some((s) => countState(s, nowMs, stuckIds) === "settling");

/**
 * When a stopped scrape can be asked about: three minutes after the scraper's last write. null: there is no
 * sign of a scraper on its record, so there is nothing to wait for.
 */
export const stillAt = (scrape) => { const ran = lastRunOf(scrape); return ran === null ? null : ran + SETTLE_MS; };

/**
 * Whether a scrape that needs a count may be asked about NOW (`nowMs`: the browser's clock). A completed scrape:
 * at once, as before. A stopped one: only when the scraper has been still for three minutes, so that a scrape
 * is not counted over and over while a cancelled scrape's box winds down.
 */
export function readyToCount(scrape, nowMs) {
  if (!needsCount(scrape)) return false;
  if (scrape.status === "completed") return true;
  const at = stillAt(scrape);
  return at === null || nowMs >= at;
}

/**
 * What "Try Again" writes on a scrape's record to have it run again (ScrapeDetailPage): back to waiting, with
 * every sign of its last run cleared.
 * ⚠️ AND ITS COUNT (`matchSummary: null`). Until 12 Oct 2026 a scrape that could be run again never held a
 * count: only a completed one did. Once a stopped scrape is counted, a retry that left the count behind left
 * the count of an EARLIER run on a scrape that is about to save more, and three orders of events then showed
 * that old count for good (the second review of PR #136): the scrape completes and the trigger that writes
 * the new count fails (a completed scrape's count always stands and is never asked for again); it is retried
 * twice and stopped before its box has written a start time or a heartbeat (nothing to say the count is old);
 * or its box's clock runs slow. With the count cleared, a retried scrape holds none: stopped again, it is
 * asked about plainly; completed, the trigger writes; and if the trigger fails the page asks, as it always
 * has for a completed scrape with no count.
 */
export const RETRY_RESET = Object.freeze({
  status: "pending",
  claimedBy: null,
  claimedAt: null,
  executingScraperId: null,
  scraperInstance: null,
  error: null,
  startedAt: null,
  completedAt: null,
  heartbeat: null,
  matchSummary: null,
});

/**
 * Whether the page should ask for a scrape's count: a completed scrape with none (as before), or a
 * stopped scrape with saved homes whose count does not stand. ⚠️ Never a scrape that is running or
 * waiting: the function would store the count of a scrape that is still growing.
 */
export function needsCount(scrape) {
  if (!scrape) return false;
  if (scrape.status === "completed") return !scrape.matchSummary;
  return isStopped(scrape) && savedOf(scrape) > 0 && !countStands(scrape);
}

/**
 * Whether the page must ask for a scrape to be counted AGAIN: it needs a count and already HOLDS one, which
 * does not stand (made before the scrape last ran). ⚠️ The function leaves a scrape that holds a count alone
 * unless it is told to count it again (`force`): asked plainly, it would answer and change nothing, and the
 * scrape would stay "not counted yet" for good.
 */
export const needsCountAgain = (scrape) => needsCount(scrape) && Boolean(scrape.matchSummary);

/**
 * One ask of each KIND per scrape per RUN: a stopped scrape that is run again and stops again is asked about
 * again, without a reload. (A completed scrape is asked about once, as before.)
 * ⚠️ The plain ask and the ask to count AGAIN are remembered apart ("!"). A scrape can be asked about plainly
 * and then be found to hold a count that does not stand: the scraper wrote once more after the page asked
 * (a cancelled scrape's scraper has not yet noticed), the plain ask was answered "it holds one already", and
 * the count that then arrives is from before that write. That needs the second kind of ask, for the same run.
 */
export const countAskKey = (scrape) => (isStopped(scrape) ? `${scrape.id}@${lastRunOf(scrape) ?? ""}${scrape.matchSummary ? "!" : ""}` : String(scrape.id));

/** The colour of the line under a stopped scrape's figure in the Scrapes list: amber, as the rows' warnings are. */
export const STOPPED_NOTE_COLOUR = "#b45309";

/**
 * What to say of a stopped scrape beside its figure, so that the count of a scrape that stopped is never
 * read as a finished one's: "stopped at search 4 of 15", or "stopped early" when the record does not say
 * where. null for a scrape that did not stop.
 */
export function stoppedNote(scrape) {
  if (!isStopped(scrape)) return null;
  const how = scrape.status === "cancelled" ? "was cancelled" : "stopped";
  const at = searchReading(scrape);
  return at.several && at.inHand !== null ? `${how} at search ${at.inHand.toLocaleString("en-GB")} of ${at.total.toLocaleString("en-GB")}` : `${how} early`;
}

/**
 * The scrapes that are in at least one of these groups: what the Groups view is handed.
 * ⚠️ From EVERY scrape, never from the list the Scrapes view's filters leave. Those filters
 * (the last 30 days, by default) are not on screen in the Groups view; handed the filtered
 * list, a group lost a scrape from its sums the day the scrape turned 31 days old.
 */
export function scrapesInGroups(scrapes, groups) {
  const ids = new Set((groups || []).map((g) => g && g.id).filter(Boolean));
  return (scrapes || []).filter((s) => Array.isArray(s && s.groupIds) && s.groupIds.some((id) => ids.has(id)));
}

/**
 * WHY a scrape with homes is in no mailable figure (EST-209, 12 Oct 2026). Lee read "2 scrapes not
 * counted", in the warning colour, as a fault: "even though i've assigned them to this group". Both
 * were still RUNNING, which is no fault at all: a scrape is counted when it finishes.
 *  - "unfinished": running, claimed, or waiting to run. Its homes are counted when it finishes.
 *  - "finished":   completed, and its count by mailing reason is not on its record yet. The page
 *                  asks for it (useScrapeMatchSummaries); until it arrives the homes are not counted.
 *  - "settling":   failed or cancelled a moment ago. The page waits until its scraper has been still for
 *                  three minutes before it asks (SETTLE_MS). No fault: said plainly, with when it ends.
 *  - "stopped":    failed or cancelled, past its still time, and its count is not on its record yet (or was made before
 *                  it last ran). The page asks for it, as it does for a finished one (12 Oct 2026;
 *                  until then a stopped scrape was never counted at all).
 *  - "status":     a status this page does not know. ⚠️ Never counted and never asked about: it may
 *                  be a scrape that is still saving homes.
 */
export function notCountedKind(scrape) {
  const status = scrape && scrape.status;
  if (status === "running" || status === "claimed" || status === "pending") return "unfinished";
  if (status === "completed") return "finished";
  if (isStopped(scrape)) return "stopped";
  return "status";
}

/**
 * The sums over a group's scrapes.
 *
 * ⚠️ SAVED IS NOT MAILABLE (9 Oct 2026). A scrape's "saved" counts every home it saved with an
 * address, whatever the grade of its match. A group's row showed saved alone and judged the
 * target by it: "3,231 / 2,800" for a group of which the mailing rule would add 1,752, because
 * the newer address engine grades about half of its matches "single-sale" and the mailing
 * default leaves those out. Lee: "far too few … where have they gone?". So the sums now carry
 * the MAILABLE figures beside the saved ones (by the mailing rule's default, no switch on),
 * and how many scrapes could not be counted, so that a figure missing some scrapes is said to
 * be "at least" and never passed off as the whole.
 */
export function groupTotals(scrapes = [], nowMs, stuckIds) {
  const totals = { totalSaved: 0, totalSstc: 0, totalUnderOffer: 0, totalSearchResults: 0,
    totalSkippedIncomplete: 0, totalExistingDuplicates: 0, totalFailed: 0, inMailshots: 0,
    mailableSaved: 0, mailableSstc: 0, mailableUnderOffer: 0,
    withSingleSaved: 0, withSingleSstc: 0, withSingleUnderOffer: 0,
    countedScrapes: 0, notCountedScrapes: 0, notCountedSaved: 0,
    // the scrapes that are not counted, by WHY (notCountedKind): the three add up to the two figures above
    unfinishedScrapes: 0, unfinishedSaved: 0, unfinishedWaiting: 0,
    finishedNotCountedScrapes: 0, finishedNotCountedSaved: 0,
    stoppedScrapes: 0, stoppedSaved: 0,
    // stopped a moment ago, and waiting until the scraper has been still (told apart only when the time is given)
    settlingScrapes: 0, settlingSaved: 0,
    // will not be asked about again in this page load; and stopped with clocks that disagree
    stuckScrapes: 0, stuckSaved: 0, clockScrapes: 0, clockSaved: 0,
    unknownStatusScrapes: 0, unknownStatusSaved: 0,
    // the scrapes that stopped early and ARE counted: in the mailable figures, and said, so that the
    // homes of a scrape that did not finish are never read as a finished scrape's
    stoppedCountedScrapes: 0, stoppedCountedSaved: 0, stoppedCountedNotes: [] };
  for (const scrape of scrapes) {
    const saved = savedOf(scrape);
    totals.totalSaved += saved;
    totals.totalSstc += scrape.soldStcProperties || scrape.progress?.soldStcProperties || 0;
    totals.totalUnderOffer += figure(scrape, "underOfferProperties");
    totals.totalSearchResults += scrape.totalProperties || scrape.progress?.totalProperties || 0;
    totals.totalSkippedIncomplete += scrape.skippedProperties || scrape.progress?.skippedProperties || 0;
    totals.totalExistingDuplicates += scrape.existingProperties || scrape.progress?.existingProperties || 0;
    totals.totalFailed += scrape.failedProperties || scrape.progress?.failedProperties || 0;
    totals.inMailshots += scrape.mailshotStats?.inMailshots || 0;
    const mailable = mailableOfScrape(scrape);
    if (mailable) {
      totals.mailableSaved += mailable.total;
      totals.mailableSstc += mailable.sstc;
      totals.mailableUnderOffer += mailable.underOffer;
      // the same count with the ONE switch on: single-sale matches included. Not the medium ones.
      const withSingle = mailableOfScrape(scrape, WITH_SINGLE);
      totals.withSingleSaved += withSingle.total;
      totals.withSingleSstc += withSingle.sstc;
      totals.withSingleUnderOffer += withSingle.underOffer;
      totals.countedScrapes += 1;
      if (isStopped(scrape)) {
        totals.stoppedCountedScrapes += 1;
        totals.stoppedCountedSaved += saved;
        totals.stoppedCountedNotes.push(stoppedNote(scrape));
      }
    } else if (saved > 0) {
      // a scrape with homes and no count: its homes are in "saved" and in NO mailable figure
      totals.notCountedScrapes += 1;
      totals.notCountedSaved += saved;
      const state = countState(scrape, nowMs, stuckIds);
      if (state === "running") {
        totals.unfinishedScrapes += 1;
        totals.unfinishedSaved += saved;
        if (scrape.status === "pending") totals.unfinishedWaiting += 1;
      } else if (state === "stuck") {
        totals.stuckScrapes += 1;
        totals.stuckSaved += saved;
      } else if (state === "counting" && scrape.status === "completed") {
        totals.finishedNotCountedScrapes += 1;
        totals.finishedNotCountedSaved += saved;
      } else if (state === "settling") {
        totals.settlingScrapes += 1;
        totals.settlingSaved += saved;
      } else if (state === "clock") {
        totals.clockScrapes += 1;
        totals.clockSaved += saved;
      } else if (state === "counting") {
        totals.stoppedScrapes += 1;
        totals.stoppedSaved += saved;
      } else {
        totals.unknownStatusScrapes += 1;
        totals.unknownStatusSaved += saved;
      }
    }
  }
  return totals;
}

/** How many SAVED homes are of the kind a target of this type counts. */
export function targetValueOf(type, totals) {
  if (type === "sstc") return totals.totalSstc;
  if (type === "sstc_uo") return totals.totalSstc + totals.totalUnderOffer;
  return totals.totalSaved;
}

/** How many of those the mailing rule would add by default: what the target is judged by. */
export function mailableValueOf(type, totals) {
  if (type === "sstc") return totals.mailableSstc;
  if (type === "sstc_uo") return totals.mailableSstc + totals.mailableUnderOffer;
  return totals.mailableSaved;
}

/**
 * How many it would be with single-sale matches INCLUDED, which the mailing default leaves out
 * (9 Oct 2026). The mailing default adds the strongest matches only; whether to include the
 * single-sale ones is a choice made in the add box, and the count by reason stored on each
 * scrape can say what that choice is worth for a group, without a count made by hand each
 * time. It is a figure beside the target, never what the target is judged by. Medium matches
 * are in neither figure.
 */
export function withSingleValueOf(type, totals) {
  if (type === "sstc") return totals.withSingleSstc;
  if (type === "sstc_uo") return totals.withSingleSstc + totals.withSingleUnderOffer;
  return totals.withSingleSaved;
}

/**
 * A group's target, read: the mailable figure against the target, with the saved figure beside
 * it. When some of the group's scrapes could not be counted, the mailable figure is "at least"
 * and the target is judged by what is known, so it never reads as met when it may not be.
 * @returns {{ saved, mailable, withSingle, atLeast, percent, status }} `percent` is not capped;
 *   `status` is "complete" at 100 or more, "close" from 75, else "far". Both are by `mailable`.
 *   `withSingle`: the figure with single-sale matches included; it moves neither.
 */
export function targetReading(target, type, totals) {
  const saved = targetValueOf(type, totals);
  const mailable = mailableValueOf(type, totals);
  const percent = target > 0 ? (mailable / target) * 100 : 0;
  return {
    saved,
    mailable,
    withSingle: withSingleValueOf(type, totals),
    atLeast: totals.notCountedScrapes > 0,
    percent,
    status: percent >= 100 ? "complete" : percent >= 75 ? "close" : "far",
  };
}

const plural = (n, one, many) => `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;

/** The words of the groups list about mailable figures. One place, so the page and its test read the same. */
export const MAILABLE_WORDS = Object.freeze({
  note:
    "Mailable is how many of a group's homes the mailing rule would add to a mailshot by default (the strongest matches only), " +
    "as counted when each scrape finished. A target is judged by it, not by the number saved. " +
    "It is the mailing rule only: the price limit, the box that leaves out developments, and the check for doubles and do not contact take a little more. " +
    "\"With single-sale\" is how many it would be with single-sale matches included, which the mailing default leaves out; medium matches are in neither figure.",
  mailable: "mailable",
  withSingle: "with single-sale",
  saved: "saved",
  atLeast: "at least",
});

/**
 * What to say of the scrapes a group's mailable figures leave out, or null when none is left out:
 * a block for each KIND of reason (notCountedKind), each a few short lines a narrow cell can
 * hold, with a tone.
 *
 *   [{ kind: 'unfinished'|'settling'|'finished'|'stopped'|'clock'|'stuck'|'status'|'unknown', tone: 'plain'|'warn', lines: string[] }]
 *
 * ⚠️ A scrape that is STILL RUNNING is not a fault, and is not said in the warning colour: it
 * read "2 scrapes not counted" in dark red, and was taken for one (EST-209). The warning colour
 * is kept for a scrape that has finished or stopped and has no count.
 * ⚠️ A running scrape's saved homes are of EVERY sale status. Where the target counts Sold STC
 * (or Sold STC + Under Offer) the line says so, or "3,286 saved homes" reads as 3,286 towards a
 * target that most of them do not count for.
 * `type`: the group's kind of target (as TARGET_TYPES).
 */
export function notCountedLines(totals, type) {
  if (!totals || !(totals.notCountedScrapes > 0)) return null;
  const blocks = [];
  const byStatus = type === "sstc" || type === "sstc_uo";
  if (totals.unfinishedScrapes > 0) {
    const count = totals.unfinishedScrapes;
    blocks.push({
      kind: "unfinished",
      tone: "plain",
      lines: [
        `${plural(count, "scrape", "scrapes")} ${totals.unfinishedWaiting > 0 ? "not finished yet" : "still running"}`,
        `${plural(totals.unfinishedSaved, "saved home", "saved homes")}${byStatus ? " of every status" : ""},`,
        `counted when ${count === 1 ? "it finishes" : "each finishes"}`,
      ],
    });
  }
  if (totals.settlingScrapes > 0) {
    const count = totals.settlingScrapes;
    blocks.push({
      kind: "settling",
      tone: "plain",
      lines: [
        `${plural(count, "scrape", "scrapes")} stopped a moment ago`,
        `(${plural(totals.settlingSaved, "saved home", "saved homes")})`,
        `counted once ${count === 1 ? "its scraper has" : "each scraper has"} been still for ${SETTLE_MS / 60000} minutes`,
      ],
    });
  }
  if (totals.finishedNotCountedScrapes > 0) {
    blocks.push({
      kind: "finished",
      tone: "warn",
      lines: [
        `${plural(totals.finishedNotCountedScrapes, "finished scrape", "finished scrapes")} not counted yet`,
        `(${plural(totals.finishedNotCountedSaved, "saved home", "saved homes")})`,
      ],
    });
  }
  if (totals.stoppedScrapes > 0) {
    blocks.push({
      kind: "stopped",
      tone: "warn",
      lines: [
        `${plural(totals.stoppedScrapes, "scrape", "scrapes")} stopped early: not counted yet`,
        `(${plural(totals.stoppedSaved, "saved home", "saved homes")})`,
      ],
    });
  }
  if (totals.clockScrapes > 0) {
    blocks.push({
      kind: "clock",
      tone: "warn",
      lines: [
        `${plural(totals.clockScrapes, "scrape", "scrapes")} stopped: not counted`,
        `(${plural(totals.clockSaved, "saved home", "saved homes")})`,
        "its scraper's clock and this browser's disagree",
      ],
    });
  }
  if (totals.stuckScrapes > 0) {
    blocks.push({
      kind: "stuck",
      tone: "warn",
      lines: [
        `${plural(totals.stuckScrapes, "scrape", "scrapes")} could not be counted`,
        `(${plural(totals.stuckSaved, "saved home", "saved homes")})`,
        "load the page again",
      ],
    });
  }
  if (totals.unknownStatusScrapes > 0) {
    blocks.push({
      kind: "status",
      tone: "warn",
      lines: [
        `${plural(totals.unknownStatusScrapes, "scrape", "scrapes")} with a status this page does not know: not counted`,
        `(${plural(totals.unknownStatusSaved, "saved home", "saved homes")})`,
      ],
    });
  }
  // sums that do not say why (none of the seven): said as before, never dropped
  if (blocks.length === 0) {
    blocks.push({
      kind: "unknown",
      tone: "warn",
      lines: [
        `${plural(totals.notCountedScrapes, "scrape", "scrapes")} not counted`,
        `(${plural(totals.notCountedSaved, "saved home", "saved homes")})`,
      ],
    });
  }
  return blocks;
}

/**
 * The hover words of one scrape's Mailable figure in the Scrapes list. `mailable`: mailableOfScrape's answer
 * (null: no count stands); `saved`: the scraper's saved count; `counting`: what the list's switch counts;
 * `nowMs`: the time, so that a scrape that stopped a moment ago is said to be waiting for its scraper to be still.
 * ⚠️ A hover is not where a person learns that a scrape stopped: `stoppedNote` is drawn under the figure.
 */
export function mailableCellTitle(scrape, mailable, saved, counting, nowMs, stuckIds) {
  const stopped = stoppedNote(scrape);
  if (mailable) {
    return `${mailable.total} of ${saved} saved would be mailed — counting ${counting}${stopped ? `. This scrape ${stopped}: these are the homes it had saved by then` : ""}`;
  }
  const state = countState(scrape, nowMs, stuckIds);
  if (state === "settling") return `This scrape ${stopped}, a moment ago: it is counted once its scraper has been still for ${SETTLE_MS / 60000} minutes`;
  if (state === "clock") return `This scrape ${stopped}. Its scraper's clock and this browser's disagree, so this page cannot tell when to count it`;
  if (state === "stuck") return "This scrape could not be counted: load the page again";
  if (state === "status") return "This scrape has a status this page does not know: it is not counted";
  if (state === "nothing") return `This scrape ${stopped}, and its record says it saved no homes: there is nothing to count`;
  if (state === "counting") return "Counting which of these would be mailed…";
  return "Saved so far — the mailable count appears when the scrape finishes";
}

/**
 * The Scrapes list's "In view" line, worked out: the mailable sums of the scrapes in view, how many of the counted
 * ones stopped early, and where each of the others stands (countState), so that the line never says "being counted
 * now" of a scrape that is running, waiting, stuck or not going to be asked about.
 */
export function inViewReading(scrapes, opts, nowMs, stuckIds) {
  const t = { total: 0, sstc: 0, underOffer: 0, counted: 0, stoppedCounted: 0, uncounted: 0, by: { running: 0, settling: 0, clock: 0, counting: 0, stuck: 0, status: 0, nothing: 0 } };
  (scrapes || []).forEach((scrape) => {
    const m = mailableOfScrape(scrape, opts);
    if (m) {
      t.total += m.total; t.sstc += m.sstc; t.underOffer += m.underOffer; t.counted += 1;
      if (isStopped(scrape)) t.stoppedCounted += 1;
      return;
    }
    t.uncounted += 1;
    t.by[countState(scrape, nowMs, stuckIds)] += 1;
  });
  return t;
}

/** The words after the sums of that line: each kind of scrape that is in no figure, with how many. */
export function inViewNotes(reading) {
  const by = reading.by;
  return [
    by.running > 0 ? `${plural(by.running, "scrape", "scrapes")} still running` : null,
    by.settling > 0 ? `${by.settling.toLocaleString("en-GB")} stopped a moment ago` : null,
    by.counting > 0 ? `${by.counting.toLocaleString("en-GB")} being counted now` : null,
    by.stuck > 0 ? `${by.stuck.toLocaleString("en-GB")} could not be counted (load the page again)` : null,
    by.clock > 0 ? `${by.clock.toLocaleString("en-GB")} stopped, with a clock that disagrees with this browser's` : null,
    by.status > 0 ? `${by.status.toLocaleString("en-GB")} of a status this page does not know` : null,
    by.nothing > 0 ? `${by.nothing.toLocaleString("en-GB")} stopped with no saved homes` : null,
  ].filter(Boolean);
}

/**
 * What to say of the scrapes that stopped early and ARE in a group's mailable figures, or null when
 * there is none. In the plain tone: nothing is wrong, but a scrape that stopped did not finish its
 * searches, and its homes must not be read as a finished scrape's.
 *   { kind: 'stopped-counted', tone: 'plain', lines: string[] }
 */
export function stoppedCountedLines(totals) {
  if (!totals || !(totals.stoppedCountedScrapes > 0)) return null;
  const count = totals.stoppedCountedScrapes;
  const notes = Array.isArray(totals.stoppedCountedNotes) ? totals.stoppedCountedNotes.filter(Boolean) : [];
  const first = count === 1 && notes.length === 1 ? `includes 1 scrape that ${notes[0]}` : `includes ${plural(count, "scrape", "scrapes")} that stopped early`;
  return {
    kind: "stopped-counted",
    tone: "plain",
    lines: [first, `(${plural(totals.stoppedCountedSaved, "saved home", "saved homes")}, counted as ${count === 1 ? "it" : "they"} stood)`],
  };
}
